import React from "react";
import { useFormik } from "formik";
import { withRouter } from "react-router";
import * as yup from "yup";
import { InputLabel } from "../../../../shared/InputLabel/InputLabel";
import { Select } from "../../../../shared/Select/Select";
import { useTimeTrackingConfigs } from "../hooks/useTimeTrackingConfigs";
import styles from "../scss/TimeCategoriesConfiguration.module.scss";
import { useUsersQuery } from "../../../../api/queries/useUsersQuery";
import { Input } from "../../../../shared/Input/Input";
import { Button } from "../../../../shared/Button/Button";
import { Checkbox } from "../../../../shared/Checkbox/Checkbox";
import { SETTINGS_ROUTES } from "../../../../consts/routes";
import { Toggle } from "../../../../shared/Toggle/Toggle";
import { useCreateTimeTrackingGroup } from "../hooks/useCreateTimeTrackingGroup";
import { useQueryClient } from "@tanstack/react-query";
import { uiNotification } from "../../../../services/UINotificationService";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import { typeOptions } from "./TimeCategoriesConfiguration.utils";
import { QUERY_KEYS } from "../../../../consts/api";
import { InputError } from "../../../../shared/InputError/InputError";
import { useUserTimeTrackingGroups } from "../hooks/useUserTimeTrackingGroups";
import { Skeleton } from "../../../../shared/Skeleton/Skeleton";

const TimeCategoriesConfiguration = ({ history, match }) => {
  const { data } = useTimeTrackingConfigs();

  const validationSchema = yup.object({
    groupName: yup.string().when("selectionType", {
      is: (val) => val.value === "group",
      then: yup.string().required("Group Name is required"),
      otherwise: yup.string(),
    }),
    selectedUsers: yup
      .array()
      .min(1, "At least one user must be selected")
      .required("Please select at least one user"),
    configs: yup
      .array()
      .test(
        "at-least-one-config",
        "At least one Time Category must be selected",
        (configs) => configs?.some((config) => config.isAvailable),
      ),
  });

  const editGroupId = match.params.id;
  const isEditing = Boolean(editGroupId);

  const eGroup = (g) => parseInt(g.id) === parseInt(editGroupId);
  const editGroup = data?.groups?.find(eGroup) || {};

  const { data: usersOptions } = useUsersQuery({
    select: (data) =>
      data.map((user) => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.id,
      })),
  });
  const { mutate: createGroup, isLoading: isCreatingGroup } =
    useCreateTimeTrackingGroup();
  const queryClient = useQueryClient();

  const handleCreateGroup = (formData) => {
    const formattedConfigs = formData.configs
      .filter((config) => config.isAvailable)
      .map((config) => config.id);

    const formattedFormData = {
      id: editGroupId || "",
      name: formData.groupName,
      user_ids: formData.selectedUsers.map((user) => user.value),
      tracked_as_configs: formattedConfigs,
      type: formData.selectionType.value,
    };
    createGroup(formattedFormData, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QUERY_KEYS.timeclockSettings.getTimeTrackingConfigs,
        ]);
        goBack();
      },
      onError: (error) => {
        uiNotification.error(error.response.data.message);
      },
    });
  };

  const initialValues = {
    groupName: editGroup.name || "",
    selectionType:
      typeOptions.find((option) => option.value === editGroup.type) ||
      typeOptions[0],
    selectedUsers:
      editGroup.users?.map((user) => ({
        label: user.full_name,
        value: user.id,
      })) || [],
    configs: data?.configs.map((config) => ({
      id: config.id,
      displayName: config.display_name,
      isAvailable:
        config.is_default ||
        Boolean(editGroup?.tracked_as_configs?.find((c) => c.id === config.id)),
      countInReport: data?.configs?.find((c) => c.id === config.id)
        ?.is_available,
      isDefault: config.is_default,
    })),
  };

  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => handleCreateGroup(values),
  });

  const isIndividual = values.selectionType.value === "individual";
  const isUserSet = values.selectedUsers.length === 1;
  const isIndividualUserSet = isIndividual && isUserSet;

  const shouldFetchUserConfigs = !editGroupId && isIndividualUserSet;

  const { isFetching } = useUserTimeTrackingGroups(
    values.selectedUsers[0]?.value,
    {
      select: (res) => res.data,
      enabled: shouldFetchUserConfigs,
      refetchOnMount: "always",
      staleTime: 0,
      keepPreviousData: false,
      onSuccess: (data) => {
        setFieldValue(
          "configs",
          values.configs.map((config) => {
            const matched = data?.find((item) => item.id === config.id);
            return {
              ...config,
              isAvailable: !!matched || config.isDefault,
              isDefault: matched?.is_default || config.isDefault,
            };
          }),
        );
      },
    },
  );

  const goBack = () => {
    history.push(SETTINGS_ROUTES.timeClock.settings());
  };

  return (
    <div className={styles.newConfiguration}>
      <h1 className={styles.title}>Configure Time Categories for Staff</h1>
      <p className={styles.paragraph}>
        Assign by Individual Staff or Group by Role
      </p>
      <div>
        <div className={styles.selectionForm}>
          <div>
            <InputLabel>Selection Type</InputLabel>
            <Select
              value={values.selectionType}
              onChange={(value) => setFieldValue("selectionType", value)}
              options={typeOptions}
              isDisabled={isEditing}
            />
          </div>
          {values.selectionType.value === "group" && (
            <div>
              <InputLabel>Group Name</InputLabel>
              <Input
                type="text"
                value={values.groupName}
                onChange={(e) => setFieldValue("groupName", e.target.value)}
                isError={Boolean(errors.groupName)}
              />
            </div>
          )}
        </div>
        <div>
          <InputLabel>Select Staff</InputLabel>
          <Select
            options={usersOptions}
            onChange={(value) => {
              if (values.selectionType.value !== "individual") {
                setFieldValue("selectedUsers", value);
              } else {
                setFieldValue("selectedUsers", [value]);
              }
            }}
            value={values.selectedUsers}
            isMulti={values.selectionType.value !== "individual"}
            isSearchable
            isError={Boolean(errors.selectedUsers)}
            isDisabled={isEditing}
          />
          {values.selectionType.value !== "individual" && (
            <div className={styles.selectDeselectButtons}>
              <Button
                onClick={() => setFieldValue("selectedUsers", usersOptions)}
                size={"small"}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                onClick={() => setFieldValue("selectedUsers", [])}
                size={"small"}
              >
                Deselect All
              </Button>
            </div>
          )}
        </div>
        <p className={styles.paragraph}>
          Select the Eligible Time Categories for the Individual or Group &
          Configure Revenue Per Hour Reporting
        </p>
        <table className={styles.timeTrackingConfigsTable}>
          <thead>
            <tr>
              <th>Time Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <tr>
                <td>
                  <Skeleton count={6} width="90%" height={35} />
                </td>
                <td>
                  <Skeleton count={6} height={35} />
                </td>
              </tr>
            ) : (
              values?.configs?.map((config) => (
                <tr key={config.id}>
                  <td>
                    <div className={styles.timeCategoryRowData}>
                      <Toggle
                        isEnabled={config.isAvailable}
                        isDisabled={config.isDefault}
                        onChange={() => {
                          setFieldValue(
                            "configs",
                            values.configs.map((c) =>
                              c.id === config.id
                                ? { ...c, isAvailable: !c.isAvailable }
                                : c,
                            ),
                          );
                        }}
                      />
                      <span>{config.displayName}</span>
                    </div>
                  </td>
                  <td className={styles.actionsTd}>
                    <Checkbox
                      label="Count in RPH Report"
                      isDisabled
                      isChecked={config.countInReport}
                      labelClassName={styles.actionsTdCheckboxLabel}
                      onChange={() => {}}
                    />
                    {
                      <span>
                        {config.isDefault && (
                          <span className={styles.defaultText}>Default</span>
                        )}
                      </span>
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {Boolean(errors.configs) && <InputError>{errors.configs}</InputError>}
      </div>
      <div className={styles.buttonsContainer}>
        <Button size="small" variant="outlined" onClick={goBack}>
          Cancel
        </Button>
        <Button
          size="small"
          onClick={handleSubmit}
          leftAdornment={
            isCreatingGroup ? (
              <CircularProgress size="small" color="white" />
            ) : null
          }
          isDisabled={isCreatingGroup}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default withRouter(TimeCategoriesConfiguration);
